import { atom, computed, map, ReadableAtom, type MapStore, type WritableAtom } from 'nanostores';
import { computeFileModifications } from '~/utils/diff';
import { FilesStore } from './files';
import { INTEGRATION_FILE_NAME, ARCHITECTURAL_DIAGRAM_FILE_NAME } from '~/utils/constants';
import { ScrollPosition } from '~/components/CodeMirror/CodeMirror';

export type EditorDocument = {
  content: string;
  type: 'file' | 'folder';
  isBinary: boolean;
  filePath: string;
  scroll?: ScrollPosition;
};

export type ProjectDocument = EditorDocument; // need to handle undefined file contents when deleting files

export type EditorDocuments = Record<string, ProjectDocument>;

type SelectedFile = WritableAtom<string | undefined>;

/*
  The EditorStore contains the files for the current session
  currentDocument is the document that is currently being edited
*/
export class EditorStore {
  #filesStore: FilesStore;
  selectedFile: SelectedFile = atom<string | undefined>();

  files: MapStore<EditorDocuments> = map({});

  constructor(filesStore: FilesStore) {
    this.#filesStore = filesStore;

    // Subscribe to filesStore changes and update our files store
    this.#filesStore.workspaceFiles.subscribe((newFiles) => {
      this.files.set(newFiles);
    });
  }

  size = computed([this.files], (files) => {
    return Object.keys(files).length;
  });

  currentDocument = computed([this.files, this.selectedFile], (files, selectedFile) => {
    if (!selectedFile) {
      const workflowFileKey = Object.keys(files).find((key) => key.startsWith('src/workflows/'));
      if (workflowFileKey) {
        this.selectFile(workflowFileKey);
        return files[workflowFileKey];
      }
      return undefined;
    }

    return files[selectedFile];
  });

  associatedWorkspaceFile = computed([this.files, this.selectedFile], (files, selectedFile) => {
    if (!selectedFile) {
      return undefined;
    }
    return this.#filesStore.getWorkspaceFile(selectedFile);
  });

  modifiedFiles: MapStore<Record<string, string>> = map({});
  aiModifiedFiles: MapStore<Record<string, boolean>> = map({});

  markdownFile: ReadableAtom<string | undefined> = computed([this.files, this.selectedFile], (files, selectedFile) => {
    if (!selectedFile) return undefined;
    const file = files[selectedFile];
    return file?.type === 'file' && file.filePath.endsWith('.md') ? file.content : undefined;
  });

  researchFiles: ReadableAtom<EditorDocument[]> = computed([this.files], (files) => {
    return Object.values(files).filter((file) => file.filePath.includes('research'));
  });

  transformationMappingFile: ReadableAtom<EditorDocument | undefined> = computed([this.files], (files) => {
    return Object.values(files).find((file) => file.filePath.includes('transformation_mapping_visualisation.md'));
  });

  architecturalDiagramFile: ReadableAtom<EditorDocument | undefined> = computed([this.files], (files) => {
    return Object.values(files).find((file) => file.filePath.endsWith(ARCHITECTURAL_DIAGRAM_FILE_NAME));
  });

  packageJsonFile: ReadableAtom<EditorDocument | undefined> = computed([this.files], (files) => {
    return Object.values(files).find((file) => file.filePath.endsWith('package.json'));
  });

  getFileModifications() {
    if (Object.keys(this.files.get()).length === 0) {
      return undefined;
    }
    return computeFileModifications(this.files.get(), this.modifiedFiles);
  }

  resetFileModifications() {
    this.modifiedFiles.set({});
  }

  addFile(filePath: string, content: string) {
    this.files.setKey(`${filePath}`, {
      content: content,
      filePath,
      isBinary: false,
      type: 'file',
      scroll: {
        left: 0,
        top: 0,
      },
    });
    this.setAiModifiedFile(filePath, true);
  }

  rejectFile(filePath: string) {
    this.files.setKey(`${filePath}`, {
      content: this.#filesStore.getWorkspaceFile(filePath)?.content,
      filePath,
      isBinary: false,
      type: 'file',
      scroll: { left: 0, top: 0 },
    });
  }

  removeFile(filePath: string) {
    // TODO: handle undefined file contents when deleting files
    // this.files.setKey(`${filePath}`, undefined);
  }

  updateScrollPosition(filePath: string, position: ScrollPosition) {
    const documents = this.files.get();
    const documentState = documents[filePath];

    if (!documentState) {
      return;
    }

    this.files.setKey(filePath, {
      ...documentState,
      scroll: position,
    });
  }

  updateFile(filePath: string, newContent: string) {
    const files = this.files.get();
    const fileState = files[`${filePath}`];
    if (!fileState) {
      return;
    }

    // Skip update if content hasn't changed
    if (fileState.content === newContent) {
      return;
    }

    this.files.setKey(filePath, {
      ...fileState,
      content: newContent,
    });
  }

  selectFile(filePath: string | undefined) {
    this.selectedFile.set(filePath);
  }

  setModifiedFile(filePath: string, content: string) {
    this.modifiedFiles.setKey(filePath, content);
  }

  getFileContent(filePath: string): string | undefined {
    return this.files.get()[filePath]?.content;
  }

  setAiModifiedFile(filePath: string, isModified: boolean) {
    this.aiModifiedFiles.setKey(filePath, isModified);
  }

  resetAiModifiedFiles() {
    this.aiModifiedFiles.set({});
  }

  reset() {
    this.files.set({});
    this.selectedFile.set(undefined);
  }
}
